import { useEffect, useId, useRef, useState } from 'react';
import type {
  MessageDeliveryStatus,
  MessageSendErrorCode,
} from '@teo/widget-shared';
import { useWidget } from '../context/WidgetContext.js';
import type { UiStrings } from '../i18n/ui-strings.js';

type MessageStatusIconProps = {
  status: MessageDeliveryStatus;
  sendError?: MessageSendErrorCode;
};

function resolveSendErrorLabel(
  t: UiStrings,
  code?: MessageSendErrorCode,
): string {
  switch (code) {
    case 'too_large':
      return t.sendErrorTooLarge;
    case 'unsupported':
      return t.sendErrorUnsupported;
    case 'upload':
      return t.sendErrorUpload;
    default:
      return t.sendErrorGeneric;
  }
}

function SingleCheck() {
  return (
    <svg className="teo-status-icon__svg" width="15" height="11" viewBox="0 0 15 11" aria-hidden>
      <path
        d="M1 5.5 4.2 8.7 14 1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DoubleCheck() {
  return (
    <svg className="teo-status-icon__svg" width="18" height="11" viewBox="0 0 18 11" aria-hidden>
      <path
        d="M1 5.5 3.5 8 9.5 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 5.5 8 8 17 1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FailedWarningIcon() {
  return (
    <svg className="teo-status-icon__svg" width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      <circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4.2v3.2M7 9.4h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export function MessageStatusIcon({ status, sendError }: MessageStatusIconProps) {
  const { t } = useWidget();
  const className = `teo-status-icon teo-status-icon--${status}`;
  const tipId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (status === 'sending') {
    return (
      <span className={className} role="status" aria-label={t.statusSending}>
        <span className="teo-status-icon__spinner" aria-hidden />
      </span>
    );
  }

  if (status === 'failed') {
    const reason = resolveSendErrorLabel(t, sendError);
    return (
      <span className="teo-status-failed-wrap" ref={wrapRef}>
        <button
          type="button"
          className={`${className} teo-status-icon--failed-btn`}
          aria-label={`${t.statusFailed}. ${reason}`}
          aria-expanded={open}
          aria-controls={tipId}
          title={reason}
          onClick={() => setOpen((value) => !value)}
        >
          <FailedWarningIcon />
        </button>
        {open ? (
          <span id={tipId} className="teo-status-error-tip" role="tooltip">
            {reason}
          </span>
        ) : null}
      </span>
    );
  }

  const label =
    status === 'read'
      ? t.statusRead
      : status === 'delivered'
        ? t.statusDelivered
        : t.statusSent;

  return (
    <span className={className} role="status" aria-label={label}>
      {status === 'sent' ? <SingleCheck /> : <DoubleCheck />}
    </span>
  );
}
